import CardCommon from '@op/components/CardCommon';
import { EchartsCommonBox } from '@op/components/EChartCommon';
import useChartOptions from '@op/hooks/useChartOptions/useChartOptions';
import { useXAxisInfo } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/hooks';
import EventDetailsPop from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventDetailsPop';
import EventWindows from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows';
import { getXAxisAxisLabel } from '@op/utils/chart';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import * as echarts from 'echarts';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Justify, Switch } from 'tea-component';
import { useIntl } from 'umi';
import type { PrMetricBubbleEventItem, PrMetricTrendChartProps } from './types';
import './index.scss';

const TIPS_SERIES = 'TopTipsEvent';

/** 与 TopTipsEvent 同栈，仅占位对齐气泡，不参与多指标堆积 */
const EVENT_ANCHOR_STACK = '__pr_metric_event_anchor';

type TooltipParamLike = {
  seriesName?: string;
  axisValueLabel?: string | number;
  name?: string | number;
  axisValue?: string | number;
  color?: string;
  value?: unknown;
};

type BarLabelParamLike = {
  value?: unknown;
};

type XAxisOptionWithAxisLabel = echarts.XAXisComponentOption & {
  axisLabel?: echarts.XAXisComponentOption['axisLabel'];
};

function readNumericValue(value: unknown): number {
  if (Array.isArray(value)) {
    const first = value[0];
    return Number(first) || 0;
  }
  return Number(value) || 0;
}

function prTrendChartValueFormatter(value: number): string {
  return String(
    dataFormat({
      value,
      formatRule: 'thousandsInit',
      emptyValue: '-',
    }),
  );
}

const chartShowEventTopSpace: Record<
NonNullable<PrMetricTrendChartProps['dateType']>,
number
> = {
  daily: 80,
  weekly: 130,
  monthly: 100,
  hourly: 50,
};

const defaultEventSetting = {
  bubbleEvent: {} satisfies Record<string, PrMetricBubbleEventItem[]>,
  showEventAxis: false,
  showEventBubbles: false,
};

export default function PrMetricTrendChart({
  title,
  subTitle,
  categories,
  series,
  dateType = 'daily',
  loading = false,
  empty = false,
  height = 354,
  leftTitle,
  rightTitle,
  showValueLabel = false,
  valueFormatter = prTrendChartValueFormatter,
  yAxisFormatter,
  hasDownload = true,
  onDownload,
  downloadTips,
  eventData,
  chartEventSetting,
  enableEventDetailsPop = true,
  eventDetailsNav,
  /** hover 描述黑底气泡（EventItem CommonBubble）；默认 false，避免 PR overview 大图提示过长文案 */
  showEventDescriptionBubble = false,
  eventVisibilityToggleVisible = true,
}: PrMetricTrendChartProps) {
  const { formatMessage } = useIntl();
  const [eventsVisible, setEventsVisible] = useState(true);

  const bubbleEvent = chartEventSetting?.bubbleEvent ?? defaultEventSetting.bubbleEvent;
  const showEventAxisCfg = chartEventSetting?.showEventAxis ?? false;
  const showEventBubblesCfg =
    chartEventSetting?.showEventBubbles ?? defaultEventSetting.showEventBubbles;
  const effectiveShowEventAxis = eventsVisible && showEventAxisCfg;

  /**
   * 不修改 EventItem：下层 CommonBubble 在 content 为空时只渲染子节点（见 CommonBubble）。
   * PR 默认不传描述时清空 description/description_cn，即无黑色浮窗；传 showEventDescriptionBubble 为 true 则保留接口原文案。
   */
  const bubbleEventForWindows = useMemo(() => {
    if (showEventDescriptionBubble) return bubbleEvent;
    const src: Record<string, PrMetricBubbleEventItem[]> = bubbleEvent || {};
    const next: Record<string, PrMetricBubbleEventItem[]> = {};
    Object.keys(src).forEach((axis) => {
      const list = src[axis];
      next[axis] = Array.isArray(list)
        ? list.map((item) => {
          if (item !== null && typeof item === 'object') {
            return { ...item, description: undefined, description_cn: undefined };
          }
          return item;
        })
        : list;
    });
    return next;
  }, [bubbleEvent, showEventDescriptionBubble]);

  const format = eventData?.format || 'YYYY-MM-DD';
  const chartRenderList = eventData?.chartRenderEventList || {};

  const filterLike = useMemo(() => ({ date_type: dateType }), [dateType]);

  /** 与舆情 BarAndLine 一致：双 X 轴首轴为事件数量圆点（id:eventDetails），底轴为日期类目 */
  const xAxisHookData = useMemo(() => ({ xAxisData: categories }), [categories]);

  const { getXAxisList } = useXAxisInfo({
    eventData,
    chartEventSetting,
    data: xAxisHookData,
    filter: filterLike,
  });

  const metric = series[0];

  const chartRenderKeysCount = Object.keys(chartRenderList).length;

  const haveEventBubbleLayer =
    eventsVisible &&
    showEventBubblesCfg &&
    Object.keys(bubbleEvent || {}).length > 0;

  const hasEventCapability =
    (showEventBubblesCfg && Object.keys(bubbleEvent || {}).length > 0) ||
    (showEventAxisCfg && chartRenderKeysCount > 0);

  const showTitleEventToggle =
    eventVisibilityToggleVisible !== false &&
    Boolean(title);

  const eventToggleLabelText = formatMessage({
    id: 'dbh_pr_trend_toggle_event',
    defaultMessage: 'Event',
  });

  /** 无事件图层时不要求等待气泡占位 */
  const haveEventBubble = haveEventBubbleLayer;

  /**
   * 标题旁有「事件」开关且接口带有事件图层时：关事件仍保留与展开时一致的 grid，
   * 避免因 top 从大变小、bottom 在 15↔40 切换导致柱状区变高、X 轴整块上移的视觉跳动。
   */
  const stabilizeGridAroundEventToggle =
    showTitleEventToggle && hasEventCapability;

  const gridTopPx = stabilizeGridAroundEventToggle || haveEventBubble
    ? chartShowEventTopSpace[dateType]
    : 20;

  /** 锁住开关时底部统一用较大留白，不再随双轴/单轴在 15↔40 间切换 */
  // let gridBottomPx: number;
  // if (stabilizeGridAroundEventToggle) {
  //   gridBottomPx = 20;
  // } else {
  //   gridBottomPx = effectiveShowEventAxis ? 15 : 20;
  // }

  const maxTimeValue = useMemo(
    () => (categories.length > 0 ? String(categories[categories.length - 1]) : ''),
    [categories],
  );

  const echartsSeries = useMemo(() => {
    if (!metric) return [];

    const stackForAnchor = haveEventBubble ? EVENT_ANCHOR_STACK : undefined;

    const barSeries: echarts.SeriesOption[] = [
      {
        name: metric.name,
        type: 'bar',
        stack: stackForAnchor,
        data: metric.data.map((v) => (v === null || v === undefined ? null : v)),
        itemStyle: metric.color
          ? { color: metric.color }
          : { color: '#326ff0', borderRadius: [4, 4, 0, 0] },
        label: showValueLabel
          ? {
            show: true,
            position: 'top',
            formatter: (p: unknown) =>
              valueFormatter(readNumericValue((p as BarLabelParamLike).value)),
          }
          : undefined,
      },
    ];

    if (haveEventBubble) {
      barSeries.push({
        name: TIPS_SERIES,
        type: 'bar',
        stack: EVENT_ANCHOR_STACK,
        data: categories.map(() => 0),
        itemStyle: { color: 'rgba(0,0,0,0)' },
        tooltip: { show: false },
        emphasis: { disabled: true },
        legendHoverLink: false,
        silent: true,
      });
    }

    return barSeries;
  }, [
    categories,
    metric,
    haveEventBubble,
    showValueLabel,
    valueFormatter,
  ]);

  /** showEventAxis 时使用舆情同款双 X 轴（事件圆点轴 + 日期轴）；否则单轴 */
  const options = useMemo((): unknown => {
    const dailyAxisFormatter = (value: string | number): string => {
      if (dateType !== 'daily') {
        return String(value ?? '');
      }
      return dateTimeFormat(String(value ?? ''), 'day', '-', { isStrictMode: true });
    };

    const singleXAxis = {
      type: 'category' as const,
      boundaryGap: true,
      data: categories,
      axisLine: {
        lineStyle: { type: 'solid', color: 'rgba(0, 0, 0, 0.25)', width: 1 },
        onZero: false,
      },
      axisTick: { alignWithLabel: true },
      axisLabel: {
        color: '#6A6D7E',
        fontSize: 12,
        align: 'center',
        ...getXAxisAxisLabel(filterLike),
        formatter: dailyAxisFormatter,
      },
    };

    const xAxisOption = effectiveShowEventAxis
      ? getXAxisList()
      : singleXAxis;

    if (effectiveShowEventAxis && Array.isArray(xAxisOption)) {
      const dateAxis = xAxisOption[1] as XAxisOptionWithAxisLabel;
      if (dateAxis?.axisLabel) {
        dateAxis.axisLabel = {
          ...dateAxis.axisLabel,
          color: '#6A6D7E',
          formatter: dailyAxisFormatter,
        };
      }
    }

    return {
      legend: { show: false },
      tooltip: {
        trigger: 'axis',
        confine: true,
        className: 'dbd-echarts-tooltip-container',
        formatter: (params: unknown) => {
          const list = Array.isArray(params) ? params : [params];
          const validList = list.filter(
            (p) => {
              const item = p as TooltipParamLike;
              return item?.seriesName && item.seriesName !== TIPS_SERIES;
            },
          );
          if (validList.length === 0) return '';
          const first = validList[0] as TooltipParamLike;
          const axisLabel =
            first?.axisValueLabel ?? first?.name ?? first?.axisValue;
          const rows = validList.map((p) => {
            const item = p as TooltipParamLike;
            return {
              name: item.axisValue,
              color: item.color,
              seriesName: item.seriesName,
              value: readNumericValue(item.value),
            };
          });
          return getEchartTooltipString({
            table: {
              columns: [
                { key: 'seriesName', header: '', showIcon: true, align: 'left' },
                { key: 'value', align: 'right', dataFormatRule: 'thousandsInit' },
              ],
              data: rows,
              title: axisLabel,
              hideHeader: true,
            },
          });
        },
      },
      xAxis: xAxisOption,
      yAxis: {
        type: 'value',
        splitLine: {
          show: !haveEventBubble,
          lineStyle: { type: 'dashed', color: '#E5E5E5' },
        },
        axisLabel: {
          color: '#838697',
          formatter: (v: number) => (yAxisFormatter ?? valueFormatter)(v),
        },
      },
      grid: {
        left: 0,
        right: 12,
        top: gridTopPx,
        bottom: 10,
        containLabel: true,
      },
      series: echartsSeries,
    };
  }, [
    categories,
    chartEventSetting,
    dateType,
    echartsSeries,
    effectiveShowEventAxis,
    eventData?.chartRenderEventList,
    filterLike,
    getXAxisList,
    // gridBottomPx,
    gridTopPx,
    haveEventBubble,
    valueFormatter,
    yAxisFormatter,
  ]);

  const mergeOptions = useChartOptions({
    userOptions: options as echarts.EChartsOption,
    type: 'bar',
    excludeOptions: ['xAxis', 'yAxis', 'legend', 'grid'],
  });

  const reactEchartsRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const [myChart, setMyChart] = useState<echarts.ECharts | null>(null);
  /** 首屏若在无事件图层时不必等待气泡层挂载 */
  const [loadingEvent, setLoadingEvent] = useState(
    () => hasEventCapability,
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showChart =
    !empty &&
    categories.length > 0 &&
    series.length > 0 &&
    Boolean(metric) &&
    metric.data.length === categories.length;

  /**
   * showChart / mergeOptions 变化时：无数据则销毁；有数据则保证单实例并 setOption。
   * mergeOptions 变化不再 dispose，避免 hover / 配置抖动时整图重建导致闪烁。
   */
  useEffect(() => {
    if (!showChart) {
      chartRef.current?.dispose();
      chartRef.current = null;
      setMyChart(null);
      return undefined;
    }
    const el = reactEchartsRef.current;
    if (!el) {
      return undefined;
    }
    if (!chartRef.current) {
      const inst = echarts.init(el);
      chartRef.current = inst;
      inst.on('finished', () => {
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = setTimeout(() => setLoadingEvent(false), 200);
      });
      setMyChart(inst);
    }
    chartRef.current.setOption(mergeOptions, true);

    const handleResize = debounce(() => {
      chartRef.current?.resize();
    }, 400);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showChart, mergeOptions]);

  useEffect(
    () => () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    },
    [],
  );

  const showAxisEventDetailsLayers =
    enableEventDetailsPop &&
    effectiveShowEventAxis &&
    chartRenderKeysCount > 0;

  const eventPopAnchorRef = useRef<HTMLDivElement>(null);

  const cardTitle = useMemo(() => {
    if (!showTitleEventToggle) return title;
    return (
      <span className="dbh-pr-metric-trend__title-row">
        <span className="dbh-pr-metric-trend__title-text">{title}</span>
        <span
          className="dbh-pr-metric-trend__event-toggle"
          role="presentation"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            value={eventsVisible}
            onChange={(v) => {
              setEventsVisible(v);
              setLoadingEvent(Boolean(v));
            }}
          />
          <span className="dbh-pr-metric-trend__event-toggle-label">{eventToggleLabelText}</span>
        </span>
      </span>
    );
  }, [
    title,
    showTitleEventToggle,
    eventsVisible,
    eventToggleLabelText,
  ]);

  useEffect(() => {
    if (!haveEventBubble) {
      setLoadingEvent(false);
    }
  }, [haveEventBubble]);

  /**
   * 当鼠标进入 anchor 热区时，手动给内部 .dbo-event-floater 触发 mouseover，
   * 让 EventDetailsPop 内部的 isMouseOverPopup 保持 true，弹窗不消失。
   */
  const handleAnchorMouseEnter = useCallback(() => {
    const floater = eventPopAnchorRef.current?.querySelector('.dbo-event-floater');
    if (floater) {
      floater.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
    }
  }, []);

  const handleAnchorMouseLeave = useCallback(() => {
    const floater = eventPopAnchorRef.current?.querySelector('.dbo-event-floater');
    if (floater) {
      floater.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
    }
  }, []);

  const chartBody = showChart ? (
    <>
      {((leftTitle !== null && leftTitle !== undefined) || (rightTitle !== null && rightTitle !== undefined)) && (
        <Justify
          className="dbo-bar-line-title-info"
          left={leftTitle}
          right={rightTitle}
        />
      )}
      <div ref={wrapRef} className="dbh-pr-metric-trend-chart-wrap">
        <EchartsCommonBox height={height}>
          <div ref={reactEchartsRef} style={{ width: '100%', height }} />
        </EchartsCommonBox>
        {!loadingEvent && myChart && haveEventBubble && (
          <EventWindows
            myChart={myChart}
            eventList={bubbleEventForWindows}
            format={format}
            maxTimeValue={maxTimeValue}
            showEventAxis={effectiveShowEventAxis}
            dateType={dateType}
            xAxisData={categories}
            eventDetailsNav={eventDetailsNav}
          />
        )}
      </div>
      {!loadingEvent && myChart && showAxisEventDetailsLayers ? (
        <div
          ref={eventPopAnchorRef}
          className="dbh-pr-metric-trend-event-pop-anchor"
          onMouseEnter={handleAnchorMouseEnter}
          onMouseLeave={handleAnchorMouseLeave}
        >
          <EventDetailsPop
            myChart={myChart}
            eventList={chartRenderList as Record<string, { count: number; }>}
            dateType={dateType}
          />
        </div>
      ) : null}
    </>
  ) : null;

  return (
    <CardCommon
      className="dbh-pr-metric-trend dbo-card-common--by-sentiment "
      title={cardTitle as string | JSX.Element | undefined}
      subTitle={subTitle as string | JSX.Element | undefined}
      hasDownload={hasDownload}
      handleDownloadOnClick={onDownload}
      downLoadTips={downloadTips}
      loading={loading}
      loadingHeight={height + 48}
      empty={empty || !showChart}
      emptyHeight={height}
    >
      {chartBody}
    </CardCommon>
  );
}
